import { Button, Form, Select } from "antd";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { IUser } from "../../../types";
import { showErrorMessage, showSuccessMessage } from "../../../utils";
import {
  getCommandComponent,
  getCommandInitialValues,
  getCommandMethod,
  getCommandOptions,
  getCommandSuccessHandler,
} from "./config";
import { Command, CommandFormValues } from "./types";

const getTexts = (t: TFunction<"CommandHub">) => ({
  commandSelectPlaceholder: t("commandSelectPlaceholder"),
  execute: t("button.execute"),
  executing: t("button.executing"),
  success: t("feedback.success"),
  error: t("feedback.error"),
});

const ErrorMessage = ({ error }: { error: string }) => {
  return (
    <div style={{ whiteSpace: "pre-wrap", textAlign: "left" }} className="pl-2">
      {error}
    </div>
  );
};

export const CommandForm = ({ user }: { user: IUser }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation("CommandHub");
  const texts = getTexts(t);

  const commandOptions = getCommandOptions(t);
  const [command, setCommand] = useState<Command>(commandOptions[0].value);
  const [submitting, setSubmitting] = useState(false);

  const handleCommandChange = (newCommand: Command) => {
    setCommand(newCommand);
    form.resetFields();
    form.setFieldsValue({
      command: newCommand,
      ...getCommandInitialValues(newCommand),
    });
  };

  const handleFinish = async (values: CommandFormValues) => {
    setSubmitting(true);
    try {
      const commandMethod = getCommandMethod(values.command);
      const response = await commandMethod(user, values);

      const successHandler = getCommandSuccessHandler(values.command);
      if (successHandler) {
        showSuccessMessage(successHandler(response));
      } else {
        showSuccessMessage(texts.success);
      }
    } catch (error) {
      showErrorMessage(<ErrorMessage error={(error as Error).message} />);
    } finally {
      setSubmitting(false);
    }
  };

  const CommandComponent = getCommandComponent(command);

  return (
    <Form
      form={form}
      layout="horizontal"
      labelCol={{ span: 3 }}
      wrapperCol={{ span: 6 }}
      onFinish={handleFinish}
      initialValues={{
        command,
        ...getCommandInitialValues(command),
      }}
      disabled={submitting}
    >
      <Form.Item label="Command" name="command">
        <Select
          data-testid="command-select"
          showSearch
          placeholder={texts.commandSelectPlaceholder}
          optionFilterProp="label"
          options={commandOptions}
          onChange={handleCommandChange}
        />
      </Form.Item>

      {CommandComponent && (
        <div data-testid={`${command}Component`}>
          <CommandComponent />
        </div>
      )}

      <Form.Item wrapperCol={{ offset: 3, span: 6 }}>
        <Button
          htmlType="submit"
          type="primary"
          loading={submitting}
          className="mt-4"
          data-testid="execute-button"
          data-testvalue={submitting ? "executing" : "execute"}
        >
          {submitting ? texts.executing : texts.execute}
        </Button>
      </Form.Item>
    </Form>
  );
};
